import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { LICENSE_CONCLUDED } from '../../../../shared/featureFlags/flagNames';
import { IssueFilterKeys, type LicensingIssueType } from '../../../../shared/issuesV2';
import { useIsFeatureEnabled } from '../../../helpers/features';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import { FilterPanel } from '../../generic/Filter';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption } from '../../../types';
import type { SelectedOptionsProps } from '../../generic/Filter';

const FILTER_OPTIONS: SelectOption[] = [
  { value: 'policy_conflict', label: 'Denied' },
  { value: 'policy_flag', label: 'Flagged' },
  { value: 'unlicensed_dependency', label: 'Unlicensed' },
  { value: 'unconcluded_dependency', label: 'Unconcluded' },
];

const IssueTypeFilter: VoidFunctionComponent<Omit<SelectedOptionsProps, 'onSelect'>> = ({ selectedOptions }) => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilterHasChanged = useSetFilterHasChanged();
  const isLicenseConcludedEnabled = useIsFeatureEnabled(LICENSE_CONCLUDED);

  // Filter out the unconcluded dependency option if the license concluded feature flag is disabled
  const filterOptions = isLicenseConcludedEnabled
    ? FILTER_OPTIONS
    : FILTER_OPTIONS.filter((option) => option.value !== 'unconcluded_dependency');

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: filterOptions.find(filterOption => filterOption.value === option)?.label || option,
    value: option,
  }));

  const onCheckboxChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.Type]: newSelected.map(({ value }) => value as LicensingIssueType),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardCheckboxes
        label="Issue Type"
        options={filterOptions}
        values={selectedCheckboxes}
        onChange={onCheckboxChange}
        testId="issues-issue-type-filters"
      />
    </FilterPanel>
  );
};

export default IssueTypeFilter;
